import { useState } from 'react'
import { useForm } from 'react-hook-form'
import { useMutation } from '@tanstack/react-query'
import { motion, AnimatePresence } from 'framer-motion'
import { XMarkIcon, CheckCircleIcon } from '@heroicons/react/24/outline'
import { enquiryAPI } from '../../services/api'
import toast from 'react-hot-toast'

const EnquiryModal = ({ isOpen, onClose, title, source, itemName, itemType = 'item', itemId = null }) => {
    const [isSubmitted, setIsSubmitted] = useState(false)

    const {
        register,
        handleSubmit,
        formState: { errors },
        reset,
    } = useForm()

    const enquiryMutation = useMutation({
        mutationFn: enquiryAPI.createEnquiry,
        onSuccess: (data) => {
            toast.success('Enquiry submitted successfully! We will get back to you soon.')
            reset()
            setIsSubmitted(true)
            setTimeout(() => {
                setIsSubmitted(false)
                onClose()
            }, 2000)
        },
        onError: (error) => {
            toast.error(error.response?.data?.message || 'Failed to submit enquiry')
        },
    })

    const onSubmit = (data) => {
        const enquiryData = {
            ...data,
            source: source,
            consent_terms: 'true',
            message: `Enquiry for ${itemType}: ${itemName}\n\n${data.message || 'No additional message provided'}`
        }

        // Add university or course ID based on item type
        if (itemId) {
            if (itemType === 'university') {
                enquiryData.universityId = itemId
            } else if (itemType === 'course') {
                enquiryData.courseId = itemId
            }
        }

        enquiryMutation.mutate(enquiryData)
    }

    const handleClose = () => {
        if (!enquiryMutation.isLoading) {
            reset()
            setIsSubmitted(false)
            onClose()
        }
    }

    if (!isOpen) return null

    return (
        <AnimatePresence>
            <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
                onClick={handleClose}
            >
                <motion.div
                    initial={{ scale: 0.95, opacity: 0 }}
                    animate={{ scale: 1, opacity: 1 }}
                    exit={{ scale: 0.95, opacity: 0 }}
                    className="bg-white rounded-lg shadow-xl max-w-md w-full max-h-[90vh] overflow-y-auto"
                    onClick={(e) => e.stopPropagation()}
                >
                    <div className="px-6 py-4 border-b border-gray-200">
                        <div className="flex items-center justify-between">
                            <h3 className="text-lg font-semibold text-gray-900">
                                {title}
                            </h3>
                            <button
                                onClick={handleClose}
                                disabled={enquiryMutation.isLoading}
                                className="text-gray-400 hover:text-gray-600 disabled:opacity-50"
                            >
                                <XMarkIcon className="w-6 h-6" />
                            </button>
                        </div>
                    </div>

                    {isSubmitted ? (
                        <div className="px-6 py-8 text-center">
                            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                                <CheckCircleIcon className="w-8 h-8 text-green-600" />
                            </div>
                            <h4 className="text-lg font-semibold text-gray-900 mb-2">
                                Enquiry Submitted!
                            </h4>
                            <p className="text-gray-600">
                                Thank you for your interest. We will get back to you within 24 hours.
                            </p>
                        </div>
                    ) : (
                        <form onSubmit={handleSubmit(onSubmit)} className="px-6 py-4 space-y-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Full Name *
                                </label>
                                <input
                                    {...register('name', { required: 'Name is required' })}
                                    className="input"
                                    placeholder="Enter your full name"
                                />
                                {errors.name && (
                                    <p className="text-red-500 text-sm mt-1">{errors.name.message}</p>
                                )}
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Email *
                                </label>
                                <input
                                    {...register('email', {
                                        required: 'Email is required',
                                        pattern: {
                                            value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                                            message: 'Invalid email address'
                                        }
                                    })}
                                    type="email"
                                    className="input"
                                    placeholder="Enter your email"
                                />
                                {errors.email && (
                                    <p className="text-red-500 text-sm mt-1">{errors.email.message}</p>
                                )}
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Phone Number *
                                </label>
                                <input
                                    {...register('phone', {
                                        required: 'Phone number is required',
                                        pattern: {
                                            value: /^[\+]?[1-9][\d]{0,15}$/,
                                            message: 'Please enter a valid phone number (10-15 digits, optional + prefix)'
                                        }
                                    })}
                                    type="tel"
                                    className="input"
                                    placeholder="Enter your phone number"
                                />
                                {errors.phone && (
                                    <p className="text-red-500 text-sm mt-1">{errors.phone.message}</p>
                                )}
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    City *
                                </label>
                                <input
                                    {...register('city', {
                                        required: 'City is required',
                                        minLength: {
                                            value: 2,
                                            message: 'City must be at least 2 characters'
                                        },
                                        maxLength: {
                                            value: 50,
                                            message: 'City must not exceed 50 characters'
                                        }
                                    })}
                                    className="input"
                                    placeholder="Enter your city"
                                />
                                {errors.city && (
                                    <p className="text-red-500 text-sm mt-1">{errors.city.message}</p>
                                )}
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Message
                                </label>
                                <textarea
                                    {...register('message')}
                                    rows={4}
                                    className="input"
                                    placeholder="Tell us about your requirements or questions..."
                                />
                            </div>

                            <div className="flex items-center">
                                <input
                                    {...register('consent_terms', { required: 'You must agree to the terms' })}
                                    type="checkbox"
                                    className="h-4 w-4 text-accent-600 focus:ring-accent-500 border-gray-300 rounded"
                                />
                                <label className="ml-2 block text-sm text-gray-900">
                                    I agree to the <a href="/terms" className="text-accent-600 hover:text-accent-700">Terms & Conditions</a> and <a href="/privacy" className="text-accent-600 hover:text-accent-700">Privacy Policy</a>
                                </label>
                            </div>
                            {errors.consent_terms && (
                                <p className="text-red-500 text-sm">{errors.consent_terms.message}</p>
                            )}

                            <div className="flex justify-end space-x-3 pt-4">
                                <button
                                    type="button"
                                    onClick={handleClose}
                                    disabled={enquiryMutation.isLoading}
                                    className="px-4 py-2 text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50"
                                >
                                    Cancel
                                </button>
                                <button
                                    type="submit"
                                    disabled={enquiryMutation.isLoading}
                                    className="btn-primary"
                                >
                                    {enquiryMutation.isLoading ? 'Submitting...' : 'Submit Enquiry'}
                                </button>
                            </div>
                        </form>
                    )}
                </motion.div>
            </motion.div>
        </AnimatePresence>
    )
}

export default EnquiryModal
